import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { notify } from '@/lib/notify';
import { useUpdateSupplierConditions } from '../catalog/hooks/useStoreCatalog';
import type { CatalogSupplierRow } from '../catalog/types';
import { deliveryDaysError, leadTimeError, orderContactEmailError } from './supplierContact';

export function SupplierConditionsForm({ supplier }: { supplier: CatalogSupplierRow }) {
    const save = useUpdateSupplierConditions();
    const [email, setEmail] = useState(supplier.orderContactEmail ?? '');
    const [lead, setLead] = useState(supplier.leadTimeDays == null ? '' : String(supplier.leadTimeDays));
    const [minDays, setMinDays] = useState(supplier.deliveryDaysMin == null ? '' : String(supplier.deliveryDaysMin));
    const [maxDays, setMaxDays] = useState(supplier.deliveryDaysMax == null ? '' : String(supplier.deliveryDaysMax));
    const [slug, setSlug] = useState(supplier.slug);
    if (slug !== supplier.slug) {
        setSlug(supplier.slug);
        setEmail(supplier.orderContactEmail ?? '');
        setLead(supplier.leadTimeDays == null ? '' : String(supplier.leadTimeDays));
        setMinDays(supplier.deliveryDaysMin == null ? '' : String(supplier.deliveryDaysMin));
        setMaxDays(supplier.deliveryDaysMax == null ? '' : String(supplier.deliveryDaysMax));
    }

    const dirty = email !== (supplier.orderContactEmail ?? '')
        || lead !== (supplier.leadTimeDays == null ? '' : String(supplier.leadTimeDays))
        || minDays !== (supplier.deliveryDaysMin == null ? '' : String(supplier.deliveryDaysMin))
        || maxDays !== (supplier.deliveryDaysMax == null ? '' : String(supplier.deliveryDaysMax));

    const onSave = async () => {
        const emailError = orderContactEmailError(email);
        const leadError = leadTimeError(lead);
        const daysError = deliveryDaysError(minDays, maxDays);
        const error = emailError || leadError || daysError;
        if (error) {
            notify({ type: 'err', text: error });
            return;
        }
        try {
            await save.mutateAsync({
                slug: supplier.slug,
                orderContactEmail: email.trim(),
                leadTimeDays: lead.trim() === '' ? null : Number(lead.trim()),
                deliveryDaysMin: Number(minDays.trim()),
                deliveryDaysMax: Number(maxDays.trim()),
            });
            notify({ type: 'ok', text: 'Condiciones guardadas' });
        } catch (cause: unknown) {
            notify({ type: 'err', text: cause instanceof Error ? cause.message : String(cause) });
        }
    };

    return (
        <div className="max-w-md space-y-4">
            <div className="space-y-1">
                <Label htmlFor="supplier-email">Email de pedidos</Label>
                <Input
                    id="supplier-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="pedidos@proveedor.com"
                />
                {email.trim() && orderContactEmailError(email) ? (
                    <p className="text-xs text-destructive">{orderContactEmailError(email)}</p>
                ) : null}
            </div>
            <div className="space-y-1">
                <Label htmlFor="supplier-lead">Plazo de preparación (días)</Label>
                <Input
                    id="supplier-lead"
                    inputMode="numeric"
                    value={lead}
                    onChange={(event) => setLead(event.target.value)}
                />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label htmlFor="supplier-min">Entrega mínima (días)</Label>
                    <Input
                        id="supplier-min"
                        inputMode="numeric"
                        value={minDays}
                        onChange={(event) => setMinDays(event.target.value)}
                    />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="supplier-max">Entrega máxima (días)</Label>
                    <Input
                        id="supplier-max"
                        inputMode="numeric"
                        value={maxDays}
                        onChange={(event) => setMaxDays(event.target.value)}
                    />
                </div>
            </div>
            <Button type="button" disabled={!dirty || save.isPending} onClick={() => { void onSave(); }}>
                Guardar condiciones
            </Button>
        </div>
    );
}
